import React, { useEffect, useMemo, useState } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import FormBuilderModal from './FormBuilderModal';
import OnboardingQuillEditor from './OnboardingQuillEditor';
import './OnboardingWorkflow.css';
import './TemplateBuilder.css';

const FALLBACK_STAGES = ['Before Day One', 'Day One', 'First Week', 'First Month', '90-Day Review'];

const STAGE_LABELS = {
    'Before Day One': { key: 'templatesEditor.stages.preOnboarding', fallback: 'Pre-Onboarding' },
    'Day One': { key: 'templatesEditor.stages.dayOne', fallback: 'Day One' },
    'First Week': { key: 'templatesEditor.stages.firstWeek', fallback: 'First Week' },
    'First Month': { key: 'templatesEditor.stages.firstMonth', fallback: 'First Month' },
    '90-Day Review': { key: 'templatesEditor.stages.ninetyDayReview', fallback: '90-Day Review' }
};

const DEFAULT_GROUPS = [
    { key: 'new_hire', labelKey: 'templatesEditor.groups.new_hire', fallback: 'Tasks for New Hire', shortKey: 'templatesEditor.groupShort.new_hire', shortFallback: 'New Hire', icon: 'fa-user-plus', teamRole: false },
    { key: 'hr', labelKey: 'templatesEditor.groups.hr', fallback: 'Tasks for HR', shortKey: 'templatesEditor.groupShort.hr', shortFallback: 'HR', icon: 'fa-people-group', teamRole: true },
    { key: 'manager', labelKey: 'templatesEditor.groups.manager', fallback: 'Tasks for Manager', shortKey: 'templatesEditor.groupShort.manager', shortFallback: 'Manager', icon: 'fa-user-tie', teamRole: false },
    { key: 'it', labelKey: 'templatesEditor.groups.it', fallback: 'Tasks for IT', shortKey: 'templatesEditor.groupShort.it', shortFallback: 'IT', icon: 'fa-laptop-code', teamRole: true },
    { key: 'legal', labelKey: 'templatesEditor.groups.legal', fallback: 'Tasks for Legal', shortKey: 'templatesEditor.groupShort.legal', shortFallback: 'Legal', icon: 'fa-scale-balanced', teamRole: true },
    { key: 'finance', labelKey: 'templatesEditor.groups.finance', fallback: 'Tasks for Finance', shortKey: 'templatesEditor.groupShort.finance', shortFallback: 'Finance', icon: 'fa-coins', teamRole: true },
    { key: 'automations', labelKey: 'templatesEditor.groups.automations', fallback: 'Automations', shortKey: 'templatesEditor.groupShort.automations', shortFallback: 'Automations', icon: 'fa-bolt', teamRole: false }
];

const TEAM_ROLE_KEYS = ['hr', 'it', 'legal', 'finance'];
const NEW_HIRE_AUTOMATION_TYPES = new Set(['document_request', 'document_send', 'form_collect', 'meeting_schedule']);
const PURE_AUTOMATION_TYPES = new Set(['scheduled_email']);
const INLINE_MENU_WIDTH = 188;
const INLINE_MENU_HEIGHT = 150;

const TASK_TYPE_OPTIONS = [
    { id: 'human', labelKey: 'templatesEditor.taskTypes.human', fallback: 'Manual Task', icon: 'fa-user-check', automated: false },
    { id: 'document_request', labelKey: 'templatesEditor.taskTypes.document_request', fallback: 'Document Request', icon: 'fa-file-import', automated: true },
    { id: 'document_send', labelKey: 'templatesEditor.taskTypes.document_send', fallback: 'Document Send', icon: 'fa-file-export', automated: true },
    { id: 'form_collect', labelKey: 'templatesEditor.taskTypes.form_collect', fallback: 'Form Collection', icon: 'fa-clipboard-list', automated: true },
    { id: 'meeting_schedule', labelKey: 'templatesEditor.taskTypes.meeting_schedule', fallback: 'Meeting Schedule', icon: 'fa-calendar-plus', automated: true },
    { id: 'scheduled_email', labelKey: 'templatesEditor.taskTypes.scheduled_email', fallback: 'Email Automation', icon: 'fa-envelope-open-text', automated: true }
];

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const interpolate = (text, values = {}) => {
    let output = String(text || '');
    Object.entries(values).forEach(([key, value]) => {
        output = output.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(value));
    });
    return output;
};

const parseRequiredDocuments = (value) => (
    String(value || '')
        .split(/\n|,|;/)
        .map(item => item.trim())
        .filter(Boolean)
);

const normalizePeople = (people = [], options = {}) => (
    people
        .map(person => ({
            ...person,
            id: person?.id || person?.uid || person?.email || `person_${Date.now()}`,
            email: String(person?.email || '').trim(),
            name: person?.name || person?.displayName || person?.email || 'Unknown',
            department: person?.department || person?.role || person?.title || '',
            source: options.source || person?.source || (options.internal ? 'company_team' : 'external_contact'),
            roleCategory: person?.roleCategory || options.roleCategory || ''
        }))
        .filter(person => person.email)
);

const dedupePeopleByEmail = (people = []) => {
    const seen = new Set();
    return people.filter(person => {
        const email = normalizeText(person?.email);
        if (!email || seen.has(email)) return false;
        seen.add(email);
        return true;
    });
};

const getFixedInlineMenuPosition = (anchorEl) => {
    if (!anchorEl || typeof window === 'undefined') return { top: 0, left: 0 };

    const rect = anchorEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const left = Math.min(
        Math.max(8, rect.right - INLINE_MENU_WIDTH),
        Math.max(8, viewportWidth - INLINE_MENU_WIDTH - 8)
    );
    const belowTop = rect.bottom + 6;
    const top = belowTop + INLINE_MENU_HEIGHT > viewportHeight
        ? Math.max(8, rect.top - INLINE_MENU_HEIGHT - 6)
        : belowTop;

    return { top, left };
};

const normalizeTeamRoles = (teamRoles = {}) => (
    TEAM_ROLE_KEYS.reduce((acc, key) => {
        const people = Array.isArray(teamRoles?.[key]) ? teamRoles[key] : [];
        acc[key] = normalizePeople(people, { source: 'template_team', roleCategory: key }).map(person => ({
            ...person,
            roleCategory: key,
            source: 'template_team'
        }));
        return acc;
    }, {})
);

const normalizeTemplateStages = (templateStages = {}, editorStages = FALLBACK_STAGES) => {
    const normalized = {};
    editorStages.forEach(stage => {
        const stageData = templateStages?.[stage] || {};
        let tasks = [];
        if (Array.isArray(stageData.tasks)) {
            tasks = stageData.tasks;
        } else if (stageData.workflow?.tasks && typeof stageData.workflow.tasks === 'object') {
            tasks = Object.entries(stageData.workflow.tasks).map(([id, task]) => ({ id, ...task }));
        }
        normalized[stage] = {
            ...stageData,
            tasks: tasks.map((task, index) => ({
                ...task,
                id: task.id || `task_${stage.replace(/\s+/g, '_')}_${index}`,
                assigneeCategory: getTaskGroupKey(task)
            }))
        };
    });
    return normalized;
};

const normalizeGroupKey = (value) => {
    const normalized = normalizeText(value).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (normalized === 'newhire' || normalized === 'new_hire') return 'new_hire';
    if (normalized === 'human_resources' || normalized === 'people_ops' || normalized === 'people_operations') return 'hr';
    if (normalized === 'information_technology' || normalized === 'systems') return 'it';
    if (normalized === 'legal_team') return 'legal';
    if (normalized === 'finance_accounting' || normalized === 'accounting') return 'finance';
    if (['automation', 'automations', 'system', 'auto', 'system_auto'].includes(normalized)) return 'automations';
    return normalized || 'manager';
};

const getTaskGroupKey = (task = {}) => {
    const taskType = getTaskType(task);
    if (NEW_HIRE_AUTOMATION_TYPES.has(taskType)) return 'new_hire';
    if (PURE_AUTOMATION_TYPES.has(taskType)) return 'automations';
    return normalizeGroupKey(task.assigneeCategory || inferTaskGroup(task));
};

const inferTaskGroup = (task = {}) => {
    const haystack = normalizeText([
        task.assigneeCategory,
        task.assigneeDepartment,
        task.department,
        task.assigneeName,
        task.title,
        task.description,
        task.automationType
    ].join(' '));

    if (PURE_AUTOMATION_TYPES.has(task.automationType)) return 'automations';
    if (task.automationType === 'document_request' || task.documentCollectionMode === 'candidate_upload') return 'new_hire';
    if (NEW_HIRE_AUTOMATION_TYPES.has(task.automationType)) return 'new_hire';
    if (/new hire|candidate|document request|upload/.test(haystack)) return 'new_hire';
    if (/legal|compliance|contract|nda|agreement/.test(haystack)) return 'legal';
    if (/finance|accounting|payroll|bank|tax|expense/.test(haystack)) return 'finance';
    if (/(^| )it( |$)|information technology|systems|software|hardware|laptop|computer|access|email account/.test(haystack)) return 'it';
    if (/hr|human resources|people ops|people operations|welcome|orientation|handbook|policy|training/.test(haystack)) return 'hr';
    if (/manager|goal|project|review|stakeholder|1-on-1|one-on-one/.test(haystack)) return 'manager';
    return 'manager';
};

const getTaskType = (task = {}) => {
    if (task?.automationType === 'document') return 'document_send';
    if (task?.automationType === 'form') return 'form_collect';
    if (task?.automationType === 'meeting') return 'meeting_schedule';
    if (task?.automationType) return task.automationType;
    if (task?.type === 'documentRequest' || task?.portalUploadRequired || task?.documentUploadLink) return 'document_request';
    if (task?.type === 'document' && Array.isArray(task?.attachments) && task.attachments.length > 0) return 'document_send';
    if (task?.formConfig || task?.formLink) return 'form_collect';
    if (task?.meetingConfig) return 'meeting_schedule';
    if (task?.type === 'automated') return 'scheduled_email';
    return 'human';
};

const getTypeOption = (taskType) => TASK_TYPE_OPTIONS.find(option => option.id === taskType) || TASK_TYPE_OPTIONS[0];

const createInitialTaskForm = (task = null, groupKey = 'hr') => {
    const normalizedGroupKey = normalizeGroupKey(groupKey);
    const taskType = task ? getTaskType(task) : (normalizedGroupKey === 'automations' ? 'scheduled_email' : 'human');
    const requiredDocuments = Array.isArray(task?.requiredDocuments) ? task.requiredDocuments.join('\n') : '';
    const attachments = Array.isArray(task?.attachments) ? task.attachments : [];
    return {
        id: task?.id || '',
        title: task?.title || '',
        description: task?.description || '',
        taskType,
        assigneeCategory: task ? getTaskGroupKey(task) : normalizedGroupKey,
        assigneeEmail: task?.assigneeEmail || task?.assignee || '',
        assigneeName: task?.assigneeName || '',
        assigneeDepartment: task?.assigneeDepartment || '',
        assigneeSource: task?.assigneeSource || '',
        executionMode: task?.executionMode || 'scheduled',
        relativeDays: Number.isFinite(Number(task?.relativeDays)) ? Number(task.relativeDays) : 0,
        emailDraft: {
            subject: task?.emailDraft?.subject || '',
            body: task?.emailDraft?.body || ''
        },
        requiredDocumentsText: requiredDocuments,
        attachments,
        formConfig: task?.formConfig || null,
        meetingConfig: {
            title: task?.meetingConfig?.title || '',
            durationMinutes: task?.meetingConfig?.durationMinutes || 30,
            location: task?.meetingConfig?.location || '',
            notes: task?.meetingConfig?.notes || ''
        }
    };
};

const createInitialTeamForm = (roleKey = 'hr', person = null) => ({
    roleKey,
    id: person?.id || '',
    name: person?.name || '',
    email: person?.email || '',
    department: person?.department || DEFAULT_GROUPS.find(group => group.key === roleKey)?.shortFallback || ''
});

const matchesPersonGroup = (person = {}, groupKey = '') => {
    const normalizedGroup = normalizeGroupKey(groupKey);
    const directGroup = normalizeGroupKey(person.roleCategory || person.assigneeCategory || person.assignmentGroup);
    if (directGroup === normalizedGroup) return true;

    const haystack = normalizeText([
        person.department,
        person.role,
        person.title,
        person.position,
        person.name,
        person.email
    ].join(' '));

    if (normalizedGroup === 'hr') return /hr|human resources|people ops|people operations|talent|recruit/.test(haystack);
    if (normalizedGroup === 'it') return /(^|[^a-z])it([^a-z]|$)|information technology|technology|systems|hardware|software|support/.test(haystack);
    if (normalizedGroup === 'legal') return /legal|compliance|counsel|law/.test(haystack);
    if (normalizedGroup === 'finance') return /finance|accounting|payroll|tax|expense/.test(haystack);
    if (normalizedGroup === 'manager') return /manager|lead|head|director|supervisor/.test(haystack);
    return false;
};

const TemplateEditorDetailView = ({
    template,
    stages = FALLBACK_STAGES,
    onClose,
    onSave,
    companyUID,
    teamMembers = [],
    externalContacts = [],
    onSaveExternalContact,
    onRemoveExternalContact,
    t = (key, fallback) => fallback || key
}) => {
    const tr = (key, fallback, values = {}) => {
        const result = t(key, { defaultValue: fallback, ...values });
        const text = typeof result === 'string' && result !== key ? result : fallback;
        return interpolate(text, values);
    };

    const editorStages = useMemo(() => {
        const stageList = Array.isArray(stages) && stages.length > 0 ? stages : FALLBACK_STAGES;
        return stageList.slice(0, 5);
    }, [stages]);

    const buildInitialTemplateData = () => ({
        name: template?.name || '',
        department: template?.department || 'all',
        ...template,
        stages: normalizeTemplateStages(template?.stages || {}, editorStages),
        teamRoles: normalizeTeamRoles(template?.teamRoles || {})
    });

    const [activeStage, setActiveStage] = useState(editorStages[0] || FALLBACK_STAGES[0]);
    const [templateData, setTemplateData] = useState(buildInitialTemplateData);
    const [drawer, setDrawer] = useState({ open: false, kind: 'task', mode: 'add', groupKey: 'hr', task: null, roleKey: 'hr', person: null });
    const [taskForm, setTaskForm] = useState(() => createInitialTaskForm(null, 'hr'));
    const [teamForm, setTeamForm] = useState(() => createInitialTeamForm('hr'));
    const [activeMenu, setActiveMenu] = useState(null);
    const [menuPosition, setMenuPosition] = useState(null);
    const [showFormBuilder, setShowFormBuilder] = useState(false);
    const [savingTask, setSavingTask] = useState(false);
    const [taskSearch, setTaskSearch] = useState('');
    const [showTeam, setShowTeam] = useState(false);
    const [bulkNames, setBulkNames] = useState('');
    const [showBulk, setShowBulk] = useState(false);

    useEffect(() => {
        setTemplateData(buildInitialTemplateData());
        setActiveStage(editorStages[0] || FALLBACK_STAGES[0]);
        closeDrawer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template?.id, editorStages.join('|')]);

    useEffect(() => {
        if (!activeMenu) return undefined;

        const closeMenu = () => {
            setActiveMenu(null);
            setMenuPosition(null);
        };

        window.addEventListener('resize', closeMenu);
        window.addEventListener('scroll', closeMenu, true);
        return () => {
            window.removeEventListener('resize', closeMenu);
            window.removeEventListener('scroll', closeMenu, true);
        };
    }, [activeMenu]);

    const getStageLabel = (stage) => {
        const config = STAGE_LABELS[stage] || { key: `templatesEditor.stages.${stage}`, fallback: stage };
        return tr(config.key, config.fallback);
    };

    const getGroupConfig = (groupKey) => (
        DEFAULT_GROUPS.find(group => group.key === groupKey)
        || { key: groupKey, labelKey: '', fallback: tr('templatesEditor.groups.unknown', 'Tasks for {{group}}', { group: groupKey }), shortKey: '', shortFallback: groupKey, icon: 'fa-circle', teamRole: false }
    );

    const getGroupShortLabel = (groupKey) => {
        const group = getGroupConfig(groupKey);
        return group.shortKey ? tr(group.shortKey, group.shortFallback) : group.shortFallback;
    };

    const getStageTasks = (stage = activeStage, data = templateData) => {
        const stageData = data.stages?.[stage];
        if (!stageData) return [];
        if (Array.isArray(stageData.tasks)) {
            return stageData.tasks.map((task, index) => ({
                ...task,
                id: task.id || `task_${index}`,
                assigneeCategory: getTaskGroupKey(task)
            }));
        }
        if (stageData.workflow?.tasks) {
            return Object.entries(stageData.workflow.tasks).map(([id, task]) => ({
                ...task,
                id,
                assigneeCategory: getTaskGroupKey(task)
            }));
        }
        return [];
    };

    const updateStageTasks = (stage, nextTasks) => {
        setTemplateData(prev => ({
            ...prev,
            stages: {
                ...prev.stages,
                [stage]: {
                    ...(prev.stages?.[stage] || {}),
                    tasks: nextTasks
                }
            }
        }));
    };

    const templateRolePeople = useMemo(() => (
        Object.entries(templateData.teamRoles || {}).flatMap(([roleKey, people]) => (
            normalizePeople(people, { source: 'template_team', roleCategory: roleKey })
        ))
    ), [templateData.teamRoles]);

    const sharedTaskTeamRoles = useMemo(() => {
        const externalPeople = normalizePeople(externalContacts, { source: 'external_contact' });
        return TEAM_ROLE_KEYS.reduce((acc, roleKey) => {
            acc[roleKey] = externalPeople
                .filter(person => matchesPersonGroup(person, roleKey))
                .map(person => ({
                    ...person,
                    roleCategory: roleKey,
                    source: person.source || 'external_contact'
                }));
            return acc;
        }, {});
    }, [externalContacts]);

    const sharedTaskTeamPeople = useMemo(() => (
        Object.values(sharedTaskTeamRoles).flat()
    ), [sharedTaskTeamRoles]);

    const allAssigneeOptions = useMemo(() => (
        dedupePeopleByEmail([
            ...sharedTaskTeamPeople,
            ...templateRolePeople,
            ...normalizePeople(teamMembers, { internal: true, source: 'company_team' }),
            ...normalizePeople(externalContacts, { source: 'external_contact' })
        ]).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    ), [sharedTaskTeamPeople, templateRolePeople, teamMembers, externalContacts]);

    const getAssigneeOptionsForGroup = (groupKey) => {
        const normalizedGroup = normalizeGroupKey(groupKey);
        if (normalizedGroup === 'new_hire' || normalizedGroup === 'automations') return [];
        return dedupePeopleByEmail(
            allAssigneeOptions.filter(person => matchesPersonGroup(person, normalizedGroup))
        ).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    };

    const activeStageTasks = getStageTasks(activeStage);
    const activeStageIndex = editorStages.indexOf(activeStage);

    const handleInlineMenuToggle = (menuKey, event) => {
        event.stopPropagation();
        if (activeMenu === menuKey) {
            setActiveMenu(null);
            setMenuPosition(null);
            return;
        }

        setActiveMenu(menuKey);
        setMenuPosition(getFixedInlineMenuPosition(event.currentTarget));
    };

    const openTaskDrawer = (mode, groupKey, task = null) => {
        const targetGroup = normalizeGroupKey(task?.assigneeCategory || groupKey);
        setDrawer({ open: true, kind: 'task', mode, groupKey: targetGroup, task, roleKey: 'hr', person: null });
        setTaskForm(createInitialTaskForm(task, targetGroup));
        setActiveMenu(null);
    };

    const openTeamDrawer = (mode, roleKey, person = null) => {
        setDrawer({ open: true, kind: 'team', mode, groupKey: roleKey, task: null, roleKey, person });
        setTeamForm(createInitialTeamForm(roleKey, person));
        setActiveMenu(null);
    };

    const handleAddTaskAssigneeMember = async (roleKey, member = {}) => {
        const normalizedRole = normalizeGroupKey(roleKey);
        if (!TEAM_ROLE_KEYS.includes(normalizedRole)) return false;

        const email = String(member.email || '').trim().toLowerCase();
        const name = String(member.name || '').trim();
        if (!name) {
            alert(tr('templatesEditor.validation.teamNameRequired', 'Please enter a name.'));
            return false;
        }
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            alert(tr('templatesEditor.validation.teamEmailRequired', 'Please enter a valid email.'));
            return false;
        }

        const nextPerson = {
            name,
            email,
            department: getGroupShortLabel(normalizedRole),
            source: 'external_contact',
            roleCategory: normalizedRole
        };

        const savedPerson = await onSaveExternalContact?.(nextPerson);
        if (!savedPerson) return false;

        setTaskForm(prev => ({
            ...prev,
            assigneeCategory: normalizedRole,
            assigneeEmail: savedPerson.email,
            assigneeName: savedPerson.name,
            assigneeDepartment: savedPerson.department,
            assigneeSource: savedPerson.source || 'external_contact'
        }));

        return true;
    };

    function closeDrawer() {
        setDrawer(prev => ({ ...prev, open: false }));
        setSavingTask(false);
    }

    const uploadTemplateAttachments = async (attachments = [], taskId = '') => {
        if (!Array.isArray(attachments)) return [];
        const uploaded = [];
        for (const attachment of attachments) {
            if (attachment?.url && attachment?.name) {
                uploaded.push({ name: attachment.name, url: attachment.url, type: attachment.type || '' });
                continue;
            }
            const fileObj = attachment?.file;
            if (!fileObj || !companyUID) continue;
            const safeName = String(fileObj.name || attachment?.name || 'attachment').replace(/\s+/g, '_');
            const templateId = templateData?.id || template?.id || 'draft_template';
            const filePath = `companies/${companyUID}/onboarding_template_attachments/${templateId}/${activeStage}/${taskId}/${Date.now()}_${safeName}`;
            const fileRef = storageRef(storage, filePath);
            await uploadBytes(fileRef, fileObj);
            const fileURL = await getDownloadURL(fileRef);
            uploaded.push({
                name: attachment?.name || fileObj.name || 'Attachment',
                url: fileURL,
                type: fileObj.type || attachment?.type || ''
            });
        }
        return uploaded;
    };

    const buildTaskRecordFromForm = async () => {
        const selectedType = getTypeOption(taskForm.taskType);
        const isAutomated = selectedType.automated;
        const taskId = taskForm.id || `task_${Date.now()}`;
        const selectedTypeId = selectedType.id;
        const assigneeGroup = NEW_HIRE_AUTOMATION_TYPES.has(selectedTypeId)
            ? 'new_hire'
            : PURE_AUTOMATION_TYPES.has(selectedTypeId)
                ? 'automations'
                : normalizeGroupKey(taskForm.assigneeCategory);
        const selectedAssignee = allAssigneeOptions.find(person => normalizeText(person.email) === normalizeText(taskForm.assigneeEmail));
        const relativeDays = taskForm.executionMode === 'immediate' ? 0 : (Number.parseInt(taskForm.relativeDays, 10) || 0);
        const taskRecord = {
            ...(drawer.task || {}),
            id: taskId,
            title: taskForm.title.trim(),
            description: taskForm.description || '',
            type: isAutomated ? 'automated' : 'human',
            executionMode: taskForm.executionMode || 'scheduled',
            relativeDays,
            assigneeCategory: assigneeGroup,
            updatedAt: Date.now()
        };

        if (selectedAssignee) {
            taskRecord.assignee = selectedAssignee.email;
            taskRecord.assigneeEmail = selectedAssignee.email;
            taskRecord.assigneeName = selectedAssignee.name || selectedAssignee.email;
            taskRecord.assigneeDepartment = selectedAssignee.department || getGroupShortLabel(assigneeGroup);
            taskRecord.assigneeSource = selectedAssignee.source || '';
        } else {
            taskRecord.assignee = '';
            taskRecord.assigneeEmail = '';
            taskRecord.assigneeName = '';
            taskRecord.assigneeDepartment = '';
            taskRecord.assigneeSource = '';
        }

        const emailSubject = taskForm.emailDraft.subject.trim();
        const emailBody = String(taskForm.emailDraft.body || '').trim();
        if (emailSubject || emailBody) {
            taskRecord.emailDraft = { subject: emailSubject, body: emailBody };
        } else {
            delete taskRecord.emailDraft;
        }

        const requiredDocuments = parseRequiredDocuments(taskForm.requiredDocumentsText);
        if (requiredDocuments.length > 0) {
            taskRecord.requiredDocuments = requiredDocuments;
            taskRecord.documentCollectionMode = 'candidate_upload';
        } else {
            delete taskRecord.requiredDocuments;
            delete taskRecord.documentCollectionMode;
        }

        delete taskRecord.automationType;
        delete taskRecord.attachments;
        delete taskRecord.formConfig;
        delete taskRecord.meetingConfig;
        delete taskRecord.portalUploadRequired;

        if (isAutomated) {
            taskRecord.automationType = selectedTypeId;
            if (PURE_AUTOMATION_TYPES.has(selectedTypeId)) {
                taskRecord.assignee = 'System';
                taskRecord.assigneeEmail = '';
                taskRecord.assigneeName = tr('templatesManager.systemAuto', 'System (Auto)');
                taskRecord.assigneeDepartment = getGroupShortLabel('automations');
                taskRecord.assigneeSource = 'system';
            } else {
                taskRecord.assignee = '';
                taskRecord.assigneeEmail = '';
                taskRecord.assigneeName = getGroupShortLabel('new_hire');
                taskRecord.assigneeDepartment = getGroupShortLabel('new_hire');
                taskRecord.assigneeSource = 'new_hire';
            }
            if (selectedTypeId === 'document_request') {
                taskRecord.documentCollectionMode = 'candidate_upload';
                taskRecord.portalUploadRequired = true;
            }
            if (selectedTypeId === 'document_send') {
                taskRecord.attachments = await uploadTemplateAttachments(taskForm.attachments, taskId);
            }
            if (selectedTypeId === 'form_collect' && taskForm.formConfig) {
                taskRecord.formConfig = taskForm.formConfig;
            }
            if (selectedTypeId === 'meeting_schedule') {
                taskRecord.meetingConfig = {
                    title: taskForm.meetingConfig.title,
                    durationMinutes: Number.parseInt(taskForm.meetingConfig.durationMinutes, 10) || 30,
                    location: taskForm.meetingConfig.location,
                    notes: taskForm.meetingConfig.notes
                };
            }
        }

        return taskRecord;
    };

    const handleSaveTask = async () => {
        if (!taskForm.title.trim()) {
            alert(tr('templatesEditor.validation.taskNameRequired', 'Please enter a task name.'));
            return;
        }
        if (!taskForm.taskType) {
            alert(tr('templatesEditor.validation.taskTypeRequired', 'Please select a task type.'));
            return;
        }
        if (!taskForm.assigneeCategory) {
            alert(tr('templatesEditor.validation.assignmentGroupRequired', 'Please select an assignment group.'));
            return;
        }

        setSavingTask(true);
        try {
            const nextTask = await buildTaskRecordFromForm();
            const stageTasks = getStageTasks(activeStage);
            const nextTasks = drawer.mode === 'edit'
                ? stageTasks.map(task => task.id === nextTask.id ? nextTask : task)
                : [...stageTasks, nextTask];
            updateStageTasks(activeStage, nextTasks);
            closeDrawer();
        } catch (error) {
            console.error('Failed to save template task:', error);
            alert(tr('alerts.saveTemplateFailedDetailed', 'Failed to save template: {{message}}', {
                message: error?.message || 'Unknown error'
            }));
            setSavingTask(false);
        }
    };

    const handleDeleteTask = (task) => {
        if (!window.confirm(tr('alerts.removeTemplateTaskConfirm', 'Are you sure you want to remove this task from the template?'))) return;
        updateStageTasks(activeStage, getStageTasks(activeStage).filter(item => item.id !== task.id));
        setActiveMenu(null);
    };

    const handleDuplicateTask = (task) => {
        const duplicate = {
            ...task,
            id: `task_${Date.now()}`,
            title: tr('templatesEditor.copyOfTask', 'Copy of {{title}}', { title: task.title || tr('templatesEditor.untitledTask', 'Untitled Task') }),
            updatedAt: Date.now()
        };
        updateStageTasks(activeStage, [...getStageTasks(activeStage), duplicate]);
        setActiveMenu(null);
    };

    const handleSaveTeamMember = async () => {
        const email = teamForm.email.trim().toLowerCase();
        const name = teamForm.name.trim();
        if (!name) {
            alert(tr('templatesEditor.validation.teamNameRequired', 'Please enter a name.'));
            return;
        }
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            alert(tr('templatesEditor.validation.teamEmailRequired', 'Please enter a valid email.'));
            return;
        }

        const roleKey = normalizeGroupKey(teamForm.roleKey);
        const nextPerson = {
            id: teamForm.id || '',
            name,
            email,
            department: teamForm.department.trim() || getGroupShortLabel(roleKey),
            source: 'external_contact',
            roleCategory: roleKey
        };

        const savedPerson = await onSaveExternalContact?.(nextPerson);
        if (savedPerson) closeDrawer();
    };

    const handleDeleteTeamMember = (roleKey, person) => {
        onRemoveExternalContact?.(person.id || person.email);
        setActiveMenu(null);
    };

    const handleSaveTemplate = () => {
        if (!templateData.name?.trim()) {
            alert(tr('alerts.enterTemplateName', 'Please enter a template name'));
            return;
        }
        onSave({
            ...templateData,
            stages: normalizeTemplateStages(templateData.stages, editorStages),
            teamRoles: normalizeTeamRoles(templateData.teamRoles)
        });
    };

    const formatTiming = (task = {}) => {
        if (task.executionMode === 'immediate') return tr('templatesManager.immediate', 'Immediate');
        const days = Number.isFinite(Number(task.relativeDays)) ? Number(task.relativeDays) : 0;
        if (days === 0) return tr('templatesManager.dayOffsetStartDay', 'Start day');
        return days > 0
            ? tr('templatesManager.dayOffsetAfterStart', '{{count}} days after start', { count: Math.abs(days) })
            : tr('templatesManager.dayOffsetBeforeStart', '{{count}} days before start', { count: Math.abs(days) });
    };

    const getAssignedToLabel = (task = {}) => {
        const groupKey = getTaskGroupKey(task);
        if (task.type === 'automated' || task.automationType) {
            if (NEW_HIRE_AUTOMATION_TYPES.has(getTaskType(task))) return getGroupShortLabel('new_hire');
            return tr('templatesManager.systemAuto', 'System (Auto)');
        }
        if (task.assigneeName) return task.assigneeName;
        if (task.assigneeEmail || task.assignee) return task.assigneeEmail || task.assignee;
        return getGroupShortLabel(groupKey);
    };

    return (
        <div className={`template-editor-redesign template-builder ${drawer.open ? 'has-drawer' : ''}`}>
            <button className="template-manager-back template-editor-redesign-back" onClick={onClose}>
                <i className="fa-solid fa-arrow-left"></i>
                {tr('templatesManager.back', 'Back')}
            </button>

            <aside className="template-editor-stage-rail">
                <div className="template-editor-rail-heading">
                    <h2>{tr('templatesEditor.steps', 'Steps')}</h2>
                    <button
                        type="button"
                        className="template-editor-team-help template-editor-steps-help"
                        aria-label={tr('templatesEditor.stepsHelp', 'These steps define the onboarding template used for new hires. Each step has its own tasks, timing, and assignees.')}
                    >
                        <i className="fa-solid fa-question"></i>
                        <span className="template-editor-team-tooltip">
                            {tr('templatesEditor.stepsHelp', 'These steps define the onboarding template used for new hires. Each step has its own tasks, timing, and assignees.')}
                        </span>
                    </button>
                </div>
                <nav aria-label={tr('templatesEditor.steps', 'Steps')}>
                    {editorStages.map((stage, index) => (
                        <button
                            key={stage}
                            type="button"
                            className={`template-editor-stage-link ${activeStage === stage ? 'active' : ''}`}
                            onClick={() => setActiveStage(stage)}
                        >
                            <span>{getStageLabel(stage)}</span><small>{getStageTasks(stage).length}</small>
                        </button>
                    ))}
                </nav>


            </aside>

            <main className="template-editor-main">
                <header className="template-editor-main-header">
                    <div>
                        <p className="builder-template-name">{templateData.name}</p><h1>
                            {tr('templatesEditor.stepTitle', 'Step {{step}}: {{stage}}', {
                                step: activeStageIndex + 1,
                                stage: getStageLabel(activeStage)
                            })}
                        </h1>
                    </div>
                    <div className="template-editor-header-actions"><button type="button" className="template-editor-secondary-btn" aria-expanded={showTeam} onClick={() => setShowTeam(!showTeam)}>{tr('builder.externalUsers', 'External users')}</button>
                        <button type="button" className="template-editor-secondary-btn" onClick={handleSaveTemplate}>
                            <i className="fa-solid fa-check"></i>
                            {tr('templatesEditor.saveTemplate', 'Save Template')}
                        </button>

                    </div>
                </header>

                <p className="builder-save-note">{tr('builder.saveNote', 'Task changes stay in this editor until you save the template.')}</p>
                {showTeam && <div className="builder-team">                <DepartmentTeamPanel
                    tr={tr}
                    teamRoles={sharedTaskTeamRoles}
                    getGroupShortLabel={getGroupShortLabel}
                    openTeamDrawer={openTeamDrawer}
                    activeMenu={activeMenu}
                    menuPosition={menuPosition}
                    onToggleMenu={handleInlineMenuToggle}
                    onDeleteTeamMember={handleDeleteTeamMember}
                /></div>}
                <div className="builder-toolbar">
                    <input aria-label={tr('builder.search', 'Search tasks')} placeholder={tr('builder.search', 'Search tasks')} value={taskSearch} onChange={e => setTaskSearch(e.target.value)} />
                    <button className="template-editor-secondary-btn" onClick={() => setShowBulk(!showBulk)}>{tr('builder.addSeveral', 'Add several tasks')}</button>
                    <button className="template-editor-primary-btn" onClick={() => openTaskDrawer('add', 'new_hire')}>+ {tr('buttons.addTask', 'Add task')}</button>
                </div>
                {showBulk && <div className="builder-bulk">
                    <label>{tr('builder.pasteHint', 'One manual task per line. Assign owners and timing after adding.')}<textarea value={bulkNames} onChange={e => setBulkNames(e.target.value)} rows={4} /></label>
                    <button className="template-editor-primary-btn" disabled={!bulkNames.trim()} onClick={() => {
                        const additions = bulkNames.split('\n').map(s => s.trim()).filter(Boolean).map((title, index) => ({ id: 'task_' + Date.now() + '_' + index, title, type: 'human', assigneeCategory: 'new_hire', relativeDays: 0, executionMode: 'scheduled' }));
                        updateStageTasks(activeStage, [...activeStageTasks, ...additions]); setBulkNames(''); setShowBulk(false);
                    }}>{tr('builder.addToStage', 'Add to this stage')}</button>
                </div>}
                <div className="builder-table-scroll">
                    <table className="builder-table">
                        <thead><tr><th>{tr('builder.task', 'Task')}</th><th>{tr('builder.owner', 'Owner / recipient')}</th><th>{tr('builder.when', 'When')}</th><th>{tr('builder.action', 'Action')}</th><th>{tr('builder.move', 'Move to stage')}</th><th>{tr('templatesEditor.table.actions', 'Actions')}</th></tr></thead>
                        <tbody>{activeStageTasks.filter(task => String(task.title || '').toLowerCase().includes(taskSearch.toLowerCase())).map(task => {
                            const type = getTypeOption(getTaskType(task));
                            return <tr key={task.id}>
                                <td><button className="builder-task-link" onClick={() => openTaskDrawer('edit', getTaskGroupKey(task), task)}>{task.title}</button></td>
                                <td>{getAssignedToLabel(task)}</td><td>{formatTiming(task)}</td>
                                <td>{tr(type.labelKey, type.fallback)}</td>
                                <td><select aria-label={tr('builder.move', 'Move to stage') + ': ' + task.title} value={activeStage} onChange={event => {
                                    const target = event.target.value;
                                    setTemplateData(prev => ({ ...prev, stages: { ...prev.stages, [activeStage]: { ...prev.stages[activeStage], tasks: getStageTasks(activeStage).filter(item => item.id !== task.id) }, [target]: { ...prev.stages[target], tasks: [...getStageTasks(target), task] } } }));
                                }}>{editorStages.map(stage => <option key={stage} value={stage}>{getStageLabel(stage)}</option>)}</select></td>
                                <td><button className="template-row-menu-btn" aria-label={tr('templatesEditor.table.actions', 'Actions') + ': ' + task.title} onClick={event => handleInlineMenuToggle(task.id, event)}><i className="fa-solid fa-ellipsis" /></button>
                                {activeMenu === task.id && <InlineActionMenu tr={tr} position={menuPosition} onEdit={() => openTaskDrawer('edit', getTaskGroupKey(task), task)} onDuplicate={() => handleDuplicateTask(task)} onDelete={() => handleDeleteTask(task)} />}</td>
                            </tr>;
                        })}</tbody>
                    </table>
                    {activeStageTasks.length === 0 && <p className="builder-empty">{tr('builder.empty', 'Build this stage by adding a task or pasting a list of tasks.')}</p>}
                </div>
            </main>

            {drawer.open && (
                <div className="template-editor-drawer-shell">
                    {drawer.kind === 'team' ? (
                        <TeamRoleDrawer
                            tr={tr}
                            mode={drawer.mode}
                            form={teamForm}
                            setForm={setTeamForm}
                            getGroupShortLabel={getGroupShortLabel}
                            onClose={closeDrawer}
                            onSave={handleSaveTeamMember}
                        />
                    ) : (
                        <TaskDrawer
                            tr={tr}
                            mode={drawer.mode}
                            activeStage={activeStage}
                            form={taskForm}
                            setForm={setTaskForm}
                            allGroups={DEFAULT_GROUPS}
                            getGroupShortLabel={getGroupShortLabel}
                            assigneeOptions={getAssigneeOptionsForGroup(taskForm.assigneeCategory)}
                            onAddTeamMember={handleAddTaskAssigneeMember}
                            onClose={closeDrawer}
                            onSave={handleSaveTask}
                            saving={savingTask}
                            setShowFormBuilder={setShowFormBuilder}
                        />
                    )}
                </div>
            )}

            {showFormBuilder && (
                <FormBuilderModal
                    onClose={() => setShowFormBuilder(false)}
                    onSave={(formConfig) => {
                        setTaskForm(prev => ({ ...prev, formConfig }));
                        setShowFormBuilder(false);
                    }}
                    initialConfig={taskForm.formConfig}
                />
            )}
        </div>
    );
};

const DepartmentTeamPanel = ({
    tr,
    teamRoles = {},
    getGroupShortLabel,
    openTeamDrawer,
    activeMenu,
    menuPosition,
    onToggleMenu,
    onDeleteTeamMember
}) => {
    const [openRoles, setOpenRoles] = useState({});

    const toggleRole = (roleKey) => {
        setOpenRoles(prev => ({
            ...prev,
            [roleKey]: !prev[roleKey]
        }));
    };

    return (
        <section className="template-editor-team-panel">
            <div className="template-editor-team-panel-header">
                <div className="template-editor-team-panel-title">
                    <h3>{tr('builder.externalUsers', 'External users')}</h3>
                    <button
                        type="button"
                        className="template-editor-team-help"
                        aria-label={tr('templatesEditor.externalTeamHelp', 'External team members do not have direct JOBSEEQR access. They can still receive onboarding task assignments and task links.')}
                    >
                        <i className="fa-solid fa-question"></i>
                        <span className="template-editor-team-tooltip">
                            {tr('templatesEditor.externalTeamHelp', 'External team members do not have direct JOBSEEQR access. They can still receive onboarding task assignments and task links.')}
                        </span>
                    </button>
                </div>
            </div>
            <p className="builder-external-users-description">{tr('builder.externalUsersHelp', 'Add people who do not have a JOBSEEQR account. They can receive onboarding tasks and task links by email.')}</p>
            <button type="button" className="template-editor-secondary-btn builder-external-users-add" onClick={() => openTeamDrawer('add', 'hr')}>{tr('builder.addExternalUser', '+ Add external user')}</button>
            <div className="template-editor-team-roles">
                {TEAM_ROLE_KEYS.map(roleKey => {
                    const people = Array.isArray(teamRoles?.[roleKey]) ? teamRoles[roleKey] : [];
                    const isOpen = Boolean(openRoles[roleKey]);
                    return (
                        <div key={roleKey} className={`template-editor-team-role ${isOpen ? 'is-open' : ''}`}>
                            <div className="template-editor-team-role-title">
                                <button
                                    type="button"
                                    className="template-editor-team-role-toggle"
                                    onClick={() => toggleRole(roleKey)}
                                    aria-expanded={isOpen}
                                >
                                    <i className={`fa-solid ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                                    <span>{getGroupShortLabel(roleKey)}</span>
                                    <em>{people.length}</em>
                                </button>
                                <button
                                    type="button"
                                    className="template-editor-team-role-add"
                                    onClick={() => openTeamDrawer('add', roleKey)}
                                    aria-label={tr('templatesEditor.drawer.addTeamMember', 'Add Team Member')}
                                >
                                    <i className="fa-solid fa-plus"></i>
                                </button>
                            </div>
                            {isOpen && (
                                <div className="template-editor-team-role-body">
                                    {people.length === 0 ? (
                                        <p>{tr('templatesEditor.noRoleContacts', 'No contacts yet')}</p>
                                    ) : (
                                        people.map(person => {
                                            const menuKey = `team_${roleKey}_${person.id || person.email}`;
                                            return (
                                                <div key={person.id || person.email} className="template-editor-team-person">
                                                    <div>
                                                        <strong>{person.name}</strong>
                                                        <span>{person.email}</span>
                                                    </div>
                                                    <div className="template-inline-menu-wrap">
                                                        <button
                                                            type="button"
                                                            className="template-row-menu-btn"
                                                            onClick={(event) => onToggleMenu(menuKey, event)}
                                                        >
                                                            <i className="fa-solid fa-ellipsis"></i>
                                                        </button>
                                                        {activeMenu === menuKey && (
                                                            <InlineActionMenu
                                                                tr={tr}
                                                                position={menuPosition}
                                                                onEdit={() => openTeamDrawer('edit', roleKey, person)}
                                                                onDelete={() => onDeleteTeamMember(roleKey, person)}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

const InlineActionMenu = ({ tr, onEdit, onDuplicate, onDelete, position }) => (
    <div className="template-inline-menu template-inline-menu--fixed" style={position || undefined}>
        <button type="button" className="template-inline-menu-item" onClick={onEdit}>
            <i className="fa-solid fa-pen"></i>
            {tr('templatesEditor.actions.edit', 'Edit')}
        </button>
        {onDuplicate && (
            <button type="button" className="template-inline-menu-item" onClick={onDuplicate}>
                <i className="fa-solid fa-copy"></i>
                {tr('templatesEditor.actions.duplicate', 'Duplicate')}
            </button>
        )}
        <button type="button" className="template-inline-menu-item template-inline-menu-item--danger" onClick={onDelete}>
            <i className="fa-solid fa-trash"></i>
            {tr('templatesEditor.actions.delete', 'Delete')}
        </button>
    </div>
);

const TaskDrawer = ({
    tr,
    mode,
    activeStage,
    form,
    setForm,
    allGroups,
    getGroupShortLabel,
    assigneeOptions,
    onAddTeamMember,
    onClose,
    onSave,
    saving,
    setShowFormBuilder
}) => {
    const selectedType = getTypeOption(form.taskType);
    const isAutomated = selectedType.automated;
    const canAssignSpecificPerson = !['new_hire', 'manager', 'automations'].includes(form.assigneeCategory);
    const canAddTeamMember = ['it', 'legal', 'finance'].includes(normalizeGroupKey(form.assigneeCategory)) && !isAutomated;
    const selectedGroupLabel = getGroupShortLabel(form.assigneeCategory);
    const isBeforeStartStage = activeStage === 'Before Day One';
    const timingLabel = isBeforeStartStage
        ? tr('templatesEditor.drawer.daysBeforeStartDay', 'Days Before Start Day')
        : tr('templatesEditor.drawer.daysAfterStartDay', 'Days After Start Day');
    const displayedRelativeDays = form.executionMode === 'immediate'
        ? 0
        : Math.abs(Number.parseInt(form.relativeDays, 10) || 0);
    const [showNewMemberForm, setShowNewMemberForm] = useState(false);
    const [newMember, setNewMember] = useState({ name: '', email: '' });

    useEffect(() => {
        setShowNewMemberForm(false);
        setNewMember({ name: '', email: '' });
    }, [form.assigneeCategory]);

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files || []);
        setForm(prev => ({
            ...prev,
            attachments: [
                ...(prev.attachments || []),
                ...files.map(file => ({ name: file.name, type: file.type, file }))
            ]
        }));
    };

    const handleSaveNewMember = async () => {
        const added = await onAddTeamMember?.(form.assigneeCategory, newMember);
        if (added) {
            setShowNewMemberForm(false);
            setNewMember({ name: '', email: '' });
        }
    };

    return (
        <aside className="template-task-drawer">
            <div className="template-task-drawer-header">
                <div>
                    <h2>{mode === 'edit' ? tr('templatesEditor.drawer.editTask', 'Edit Task') : tr('templatesEditor.drawer.addTask', 'Add Task')}</h2>
                </div>
                <button type="button" onClick={onClose}>
                    <i className="fa-solid fa-xmark"></i>
                </button>
            </div>

            <div className="template-drawer-fields">
                <label>
                    <span>{tr('templatesEditor.drawer.taskName', 'Task Name')}</span>
                    <input
                        type="text"
                        value={form.title}
                        onChange={event => setForm(prev => ({ ...prev, title: event.target.value }))}
                        placeholder={tr('templatesEditor.drawer.taskNamePlaceholder', 'e.g., Upload required documents')}
                    />
                </label>

                <label>
                    <span>{tr('templatesEditor.drawer.taskType', 'Task Type')}</span>
                    <select
                        value={form.taskType}
                        onChange={event => {
                            const nextType = event.target.value;
                            setForm(prev => {
                                const nextGroup = NEW_HIRE_AUTOMATION_TYPES.has(nextType)
                                    ? 'new_hire'
                                    : PURE_AUTOMATION_TYPES.has(nextType)
                                        ? 'automations'
                                        : prev.assigneeCategory;
                                return {
                                    ...prev,
                                    taskType: nextType,
                                    assigneeCategory: nextGroup,
                                    assigneeEmail: '',
                                    assigneeName: '',
                                    assigneeDepartment: '',
                                    assigneeSource: ''
                                };
                            });
                        }}
                    >
                        {TASK_TYPE_OPTIONS.map(option => (
                            <option key={option.id} value={option.id}>{tr(option.labelKey, option.fallback)}</option>
                        ))}
                    </select>
                </label>

                <label>
                    <span>{tr('templatesEditor.drawer.assignmentGroup', 'Assignment Group')}</span>
                    <select
                        value={form.assigneeCategory}
                        disabled={isAutomated}
                        onChange={event => setForm(prev => ({ ...prev, assigneeCategory: event.target.value, assigneeEmail: '', assigneeName: '', assigneeDepartment: '' }))}
                    >
                        {allGroups.map(group => (
                            <option key={group.key} value={group.key}>{getGroupShortLabel(group.key)}</option>
                        ))}
                    </select>
                </label>

                {canAssignSpecificPerson && !isAutomated && (
                    <label>
                        <span>{tr('templatesEditor.drawer.assignedTo', 'Assigned To')}</span>
                        <select
                            value={form.assigneeEmail}
                            onChange={event => setForm(prev => {
                                const selected = assigneeOptions.find(person => person.email === event.target.value);
                                return {
                                    ...prev,
                                    assigneeEmail: event.target.value,
                                    assigneeName: selected?.name || '',
                                    assigneeDepartment: selected?.department || '',
                                    assigneeSource: selected?.source || ''
                                };
                            })}
                        >
                            <option value="">
                                {assigneeOptions.length > 0
                                    ? tr('templatesEditor.drawer.selectGroupMember', 'Select {{group}} team member', { group: selectedGroupLabel })
                                    : tr('templatesEditor.drawer.noGroupMembers', 'No {{group}} team members yet', { group: selectedGroupLabel })}
                            </option>
                            {assigneeOptions.map(person => (
                                <option key={person.email} value={person.email}>
                                    {person.name} ({person.email}){person.department ? ` - ${person.department}` : ''}
                                </option>
                            ))}
                        </select>
                        {canAddTeamMember && (
                            <div className="template-drawer-new-member">
                                <div className="template-drawer-or-divider">
                                    <span>{tr('templatesEditor.drawer.or', 'OR')}</span>
                                </div>
                                <button
                                    type="button"
                                    className="template-drawer-add-member-toggle"
                                    onClick={() => setShowNewMemberForm(prev => !prev)}
                                >
                                    <i className="fa-solid fa-plus"></i>
                                    {tr('templatesEditor.drawer.addGroupMember', 'Add {{group}} team member', { group: selectedGroupLabel })}
                                </button>
                                {showNewMemberForm && (
                                    <div className="template-drawer-new-member-fields">
                                        <input
                                            type="text"
                                            value={newMember.name}
                                            onChange={event => setNewMember(prev => ({ ...prev, name: event.target.value }))}
                                            placeholder={tr('templatesEditor.drawer.newMemberName', 'Name')}
                                        />
                                        <input
                                            type="email"
                                            value={newMember.email}
                                            onChange={event => setNewMember(prev => ({ ...prev, email: event.target.value }))}
                                            placeholder={tr('templatesEditor.drawer.newMemberEmail', 'Email')}
                                        />
                                        <div>
                                            <button type="button" className="template-editor-primary-btn" onClick={handleSaveNewMember}>
                                                {tr('templatesEditor.drawer.addTeamMember', 'Add Team Member')}
                                            </button>
                                            <button
                                                type="button"
                                                className="template-editor-secondary-btn"
                                                onClick={() => {
                                                    setShowNewMemberForm(false);
                                                    setNewMember({ name: '', email: '' });
                                                }}
                                            >
                                                {tr('buttons.cancel', 'Cancel')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </label>
                )}

                <div className="template-drawer-two-col">
                    <label>
                        <span>{tr('templatesEditor.drawer.timing', 'Timing')}</span>
                        <select
                            value={form.executionMode}
                            onChange={event => setForm(prev => ({ ...prev, executionMode: event.target.value, relativeDays: event.target.value === 'immediate' ? 0 : prev.relativeDays }))}
                        >
                            <option value="scheduled">{tr('templatesManager.scheduled', 'Scheduled')}</option>
                            <option value="immediate">{tr('templatesManager.immediate', 'Immediate')}</option>
                        </select>
                    </label>
                    <label>
                        <span>{timingLabel}</span>
                        <input
                            type="number"
                            min="0"
                            value={displayedRelativeDays}
                            disabled={form.executionMode === 'immediate'}
                            onChange={event => {
                                const days = Math.max(0, Number.parseInt(event.target.value, 10) || 0);
                                setForm(prev => ({
                                    ...prev,
                                    relativeDays: isBeforeStartStage ? -days : days
                                }));
                            }}
                        />
                    </label>
                </div>

                <label>
                    <span>{tr('templatesEditor.drawer.description', 'Description')}</span>
                    <textarea
                        value={form.description}
                        onChange={event => setForm(prev => ({ ...prev, description: event.target.value }))}
                        placeholder={tr('templatesEditor.drawer.descriptionPlaceholder', 'Describe what needs to happen.')}
                        rows={3}
                    />
                </label>

                {(form.taskType === 'human' || form.taskType === 'document_request') && (
                    <label>
                        <span>{tr('automatedTaskModal.requiredDocuments', 'Required Documents')}</span>
                        <textarea
                            value={form.requiredDocumentsText}
                            onChange={event => setForm(prev => ({ ...prev, requiredDocumentsText: event.target.value }))}
                            placeholder={tr('automatedTaskModal.requiredDocumentsPlaceholder', 'One document per line, e.g.\nGovernment ID\nTax Form')}
                            rows={4}
                        />
                    </label>
                )}

                {form.taskType === 'document_send' && (
                    <div className="template-drawer-upload">
                        <span>{tr('automatedTaskModal.attachDocuments', 'Attach Documents')}</span>
                        <input type="file" multiple onChange={handleFileUpload} />
                        {(form.attachments || []).length > 0 && (
                            <div className="template-drawer-attachments">
                                {form.attachments.map((attachment, index) => (
                                    <div key={`${attachment.name}_${index}`}>
                                        <span>{attachment.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => setForm(prev => ({
                                                ...prev,
                                                attachments: (prev.attachments || []).filter((_, idx) => idx !== index)
                                            }))}
                                        >
                                            <i className="fa-solid fa-xmark"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {form.taskType === 'form_collect' && (
                    <div className="template-drawer-form-builder">
                        <span>{tr('automatedTaskModal.formConfiguration', 'Form Configuration')}</span>
                        <button type="button" className="template-editor-secondary-btn" onClick={() => setShowFormBuilder(true)}>
                            <i className="fa-solid fa-list-check"></i>
                            {form.formConfig
                                ? tr('automatedTaskModal.editFormFields', 'Edit Form Fields')
                                : tr('automatedTaskModal.configureFormFields', 'Configure Form Fields')}
                        </button>
                        {form.formConfig && (
                            <p>{tr('automatedTaskModal.fieldsConfigured', '{{count}} fields configured', { count: form.formConfig.fields?.length || 0 })}</p>
                        )}
                    </div>
                )}

                {form.taskType === 'meeting_schedule' && (
                    <div className="template-drawer-meeting-grid">
                        <label>
                            <span>{tr('templatesEditor.drawer.meetingTitle', 'Meeting Title')}</span>
                            <input
                                type="text"
                                value={form.meetingConfig.title}
                                onChange={event => setForm(prev => ({ ...prev, meetingConfig: { ...prev.meetingConfig, title: event.target.value } }))}
                            />
                        </label>
                        <label>
                            <span>{tr('templatesEditor.drawer.duration', 'Duration')}</span>
                            <input
                                type="number"
                                value={form.meetingConfig.durationMinutes}
                                onChange={event => setForm(prev => ({ ...prev, meetingConfig: { ...prev.meetingConfig, durationMinutes: event.target.value } }))}
                            />
                        </label>
                        <label>
                            <span>{tr('templatesEditor.drawer.location', 'Location')}</span>
                            <input
                                type="text"
                                value={form.meetingConfig.location}
                                onChange={event => setForm(prev => ({ ...prev, meetingConfig: { ...prev.meetingConfig, location: event.target.value } }))}
                            />
                        </label>
                    </div>
                )}

                {(isAutomated || form.taskType === 'human') && (
                    <div className="template-drawer-email">
                        <span>{tr('automatedTaskModal.emailDraft', 'Email Draft')}</span>
                        <input
                            type="text"
                            value={form.emailDraft.subject}
                            onChange={event => setForm(prev => ({ ...prev, emailDraft: { ...prev.emailDraft, subject: event.target.value } }))}
                            placeholder={tr('modals.addTask.emailSubjectPlaceholder', 'Email subject...')}
                        />
                        <OnboardingQuillEditor
                            className="onboarding-quill"
                            value={form.emailDraft.body}
                            onChange={(value) => setForm(prev => ({ ...prev, emailDraft: { ...prev.emailDraft, body: value } }))}
                        />
                    </div>
                )}
            </div>

            <div className="template-task-drawer-footer">
                <button type="button" className="template-editor-secondary-btn" onClick={onClose}>
                    {tr('buttons.cancel', 'Cancel')}
                </button>
                <button type="button" className="template-editor-primary-btn" onClick={onSave} disabled={saving}>
                    {saving ? tr('templatesEditor.saving', 'Saving...') : tr('humanTaskModal.saveTask', 'Save Task')}
                </button>
            </div>
        </aside>
    );
};

const TeamRoleDrawer = ({ tr, mode, form, setForm, getGroupShortLabel, onClose, onSave }) => (
    <aside className="template-task-drawer">
        <div className="template-task-drawer-header">
            <div>
                <span>{tr('builder.externalUsers', 'External users')}</span>
                <h2>{mode === 'edit' ? tr('builder.editExternalUser', 'Edit external user') : tr('builder.addExternalUser', '+ Add external user')}</h2>
            </div>
            <button type="button" onClick={onClose}>
                <i className="fa-solid fa-xmark"></i>
            </button>
        </div>

        <div className="template-drawer-fields">
            <p>{tr('builder.externalUsersHelp', 'Add people who do not have a JOBSEEQR account. They can receive onboarding tasks and task links by email.')}</p>
            <label>
                <span>{tr('templatesEditor.drawer.teamRole', 'Team Role')}</span>
                <select value={form.roleKey} onChange={event => setForm(prev => ({ ...prev, roleKey: event.target.value }))}>
                    {TEAM_ROLE_KEYS.map(roleKey => (
                        <option key={roleKey} value={roleKey}>{getGroupShortLabel(roleKey)}</option>
                    ))}
                </select>
            </label>
            <label>
                <span>{tr('externalContacts.form.namePlaceholder', 'Full Name *')}</span>
                <input type="text" value={form.name} onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))} />
            </label>
            <label>
                <span>{tr('externalContacts.form.emailPlaceholder', 'Email Address *')}</span>
                <input type="email" value={form.email} onChange={event => setForm(prev => ({ ...prev, email: event.target.value }))} />
            </label>
            <label>
                <span>{tr('modals.addHire.department', 'Department')}</span>
                <input type="text" value={form.department} onChange={event => setForm(prev => ({ ...prev, department: event.target.value }))} />
            </label>
        </div>

        <div className="template-task-drawer-footer">
            <button type="button" className="template-editor-secondary-btn" onClick={onClose}>
                {tr('buttons.cancel', 'Cancel')}
            </button>
            <button type="button" className="template-editor-primary-btn" onClick={onSave}>
                {tr('templatesEditor.drawer.saveTeamMember', 'Save Team Member')}
            </button>
        </div>
    </aside>
);

export default TemplateEditorDetailView;
